import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { http, formatApiError, formatWeek, formatMinutes, mondayOfWeek } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Eye, Home, Clock, MapPin, Handshake, FileSignature, MessageSquare, Target } from "lucide-react";

export default function AdminReports() {
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterWeek, setFilterWeek] = useState("all");
  const [filterUser, setFilterUser] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [rp, us] = await Promise.all([http.get("/reports"), http.get("/users")]);
      setReports(rp.data);
      setUsers(us.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const weeks = useMemo(() => Array.from(new Set(reports.map((r) => r.week_start))).sort().reverse(), [reports]);

  const filtered = reports.filter((r) => {
    if (filterWeek !== "all" && r.week_start !== filterWeek) return false;
    if (filterUser !== "all" && r.user_id !== filterUser) return false;
    return true;
  });

  return (
    <Layout>
      <div className="mb-8" data-testid="admin-reports">
        <p className="label-eyebrow mb-2">Tous les rapports</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">Rapports hebdomadaires</h1>
        <p className="mt-2 text-slate-600">Consultez tous les rapports remplis par vos négociateurs.</p>
      </div>

      <Card className="p-4 mb-6 flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Semaine</div>
          <Select value={filterWeek} onValueChange={setFilterWeek}>
            <SelectTrigger data-testid="filter-week-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les semaines</SelectItem>
              {weeks.map((w) => (
                <SelectItem key={w} value={w}>{formatWeek(w)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Négociateur</div>
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger data-testid="filter-user-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les négociateurs</SelectItem>
              {users.filter((u) => u.role === "collaborator").map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">Aucun rapport pour ces critères.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Semaine</TableHead>
                <TableHead>Négociateur</TableHead>
                <TableHead className="text-center">Visites</TableHead>
                <TableHead className="text-center">Prospection</TableHead>
                <TableHead className="text-center">Type</TableHead>
                <TableHead className="text-center">Relances</TableHead>
                <TableHead className="text-center">Signatures</TableHead>
                <TableHead className="text-center">Compromis</TableHead>
                <TableHead className="text-right">Détail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} data-testid={`report-row-${r.id}`}>
                  <TableCell className="font-medium">{formatWeek(r.week_start)}</TableCell>
                  <TableCell>{r.user_name}</TableCell>
                  <TableCell className="text-center tabular-nums">{r.visites}</TableCell>
                  <TableCell className="text-center tabular-nums">{formatMinutes(r.prospection_minutes)}</TableCell>
                  <TableCell className="text-center"><Badge variant="secondary" className="capitalize">{r.prospection_type === "aucune" ? "—" : r.prospection_type}</Badge></TableCell>
                  <TableCell className="text-center tabular-nums">{r.relances_vendeurs}</TableCell>
                  <TableCell className="text-center tabular-nums">{r.signatures_en_cours}</TableCell>
                  <TableCell className="text-center tabular-nums">{r.compromis_en_cours}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => setSelected(r)} data-testid={`view-report-${r.id}`}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{selected.user_name}</DialogTitle>
                <DialogDescription>Semaine du {formatWeek(selected.week_start)}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <DetailItem icon={Home} label="Visites" value={selected.visites} />
                <DetailItem icon={Clock} label="Prospection" value={`${formatMinutes(selected.prospection_minutes)} — ${selected.prospection_type}`} />
                <DetailItem icon={MapPin} label="Secteurs" value={selected.secteurs || "—"} full />
                <DetailItem icon={MessageSquare} label="Relances vendeurs" value={selected.relances_vendeurs} />
                <DetailItem icon={FileSignature} label="Signatures en cours" value={selected.signatures_en_cours} />
                <DetailItem icon={Handshake} label="Compromis en cours" value={selected.compromis_en_cours} />
              </div>
              <div className="space-y-3 mt-5">
                {selected.relances_notes ? <NoteBlock title="Notes relances" text={selected.relances_notes} /> : null}
                {selected.signatures_notes ? <NoteBlock title="Notes signatures" text={selected.signatures_notes} /> : null}
                {selected.compromis_notes ? <NoteBlock title="Notes compromis" text={selected.compromis_notes} /> : null}
                {selected.objectifs_semaine_suivante ? <NoteBlock title="Objectifs semaine suivante" text={selected.objectifs_semaine_suivante} icon={Target} /> : null}
                {selected.commentaires ? <NoteBlock title="Commentaires" text={selected.commentaires} /> : null}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function DetailItem({ icon: Icon, label, value, full }) {
  return (
    <div className={`p-3 rounded-md border border-border bg-slate-50 ${full ? "col-span-2" : ""}`}>
      <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-widest font-semibold mb-1">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function NoteBlock({ title, text, icon: Icon }) {
  return (
    <div className="p-3 rounded-md border border-border">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1 flex items-center gap-1.5">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        {title}
      </div>
      <div className="text-sm text-slate-700 whitespace-pre-wrap">{text}</div>
    </div>
  );
}
